import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Loader2, Shield, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { Dialog } from 'primereact/dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { Combobox } from '@/components/ui/combobox';
import { useAuthStore } from '@/stores/auth.store';
import { formatDate } from '@/lib/utils';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Role {
  id: string;
  name: string;
  description?: string;
  isSystem?: boolean;
}

// POS-I330: GET /users/:id/roles answers a list of Role rows (not UserRole join rows)
type UserRoleRow = Role & { role?: Role };

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  isActive: boolean;
  lastLoginAt: string | null;
  locationId?: string | null;
  location?: { id: string; name: string } | null;
  commissionRate?: number | null;
  userRoles: { role: Role }[];
}

interface CreateUserPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleId?: string;
  locationId?: string;
  commissionRate?: number;
}

interface UpdateUserPayload {
  email?: string;
  firstName: string;
  lastName: string;
  phone: string | undefined;
  isActive: boolean;
  locationId: string | null;
  commissionRate: number | null;
  password?: string;
  roleId?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const defaultCreateForm = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  roleId: '',
  locationId: '',
  commissionRate: '' as string | number,
};

const defaultEditForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  password: '',
  roleId: '',
  isActive: true,
  locationId: '',
  commissionRate: '' as string | number,
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function UsersPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('users:create') || hasPermission('users:*');
  const canUpdate = hasPermission('users:update') || hasPermission('users:*');
  const canDelete = hasPermission('users:delete') || hasPermission('users:*');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [createForm, setCreateForm] = useState({ ...defaultCreateForm });
  const [editForm, setEditForm] = useState({ ...defaultEditForm });

  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    id: string;
    name: string;
  }>({ open: false, id: '', name: '' });

  // POS-I333: inline messages under the fields (no browser bubbles); a server sentence is mapped to its field
  type FieldErrors = Partial<Record<'email' | 'password' | 'firstName' | 'lastName' | 'form', string>>;
  const [createErrors, setCreateErrors] = useState<FieldErrors>({});
  const [editErrors, setEditErrors] = useState<FieldErrors>({});
  const fieldFromMessage = (message: string): keyof FieldErrors => {
    const m = message.toLowerCase();
    if (m.includes('e-mail') || m.includes('email')) return 'email';
    if (m.includes('first name')) return 'firstName';
    if (m.includes('last name')) return 'lastName';
    if (m.includes('password')) return 'password';
    return 'form';
  };

  const queryClient = useQueryClient();

  /* ----- queries ----- */

  const { data, isLoading } = useQuery({
    queryKey: ['users', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const res = await api.get(`/users?${params}`);
      return res.data;
    },
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await api.get('/roles');
      return data as Role[];
    },
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations-simple'],
    queryFn: () => api.get('/locations').then((r) => {
      const d = r.data;
      return (Array.isArray(d) ? d : d?.data ?? []) as { id: string; name: string }[];
    }),
  });

  const { data: userRoles, isLoading: userRolesLoading } = useQuery({
    queryKey: ['user-roles', selectedUser?.id],
    queryFn: async () => {
      const { data } = await api.get(`/users/${selectedUser!.id}/roles`);
      return (Array.isArray(data) ? data : []) as UserRoleRow[];
    },
    enabled: rolesOpen && !!selectedUser,
  });

  /* ----- form sync for edit ----- */

  useEffect(() => {
    if (editOpen && selectedUser) {
      setEditForm({
        email: selectedUser.email ?? '',
        firstName: selectedUser.firstName ?? '',
        lastName: selectedUser.lastName ?? '',
        phone: selectedUser.phone ?? '',
        password: '',
        roleId: selectedUser.userRoles?.length === 1 ? selectedUser.userRoles[0].role.id : '',
        isActive: selectedUser.isActive ?? true,
        locationId: selectedUser.locationId ?? '',
        commissionRate: selectedUser.commissionRate != null ? selectedUser.commissionRate : '',
      });
    }
  }, [editOpen, selectedUser]);

  /* ----- mutations ----- */

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: CreateUserPayload = {
        email: createForm.email,
        password: createForm.password,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
      };
      if (createForm.phone) payload.phone = createForm.phone;
      if (createForm.roleId) payload.roleId = createForm.roleId;
      if (createForm.locationId) payload.locationId = createForm.locationId;
      if (createForm.commissionRate !== '' && createForm.commissionRate !== null) payload.commissionRate = Number(createForm.commissionRate);
      const { data } = await api.post('/users', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(t('userCreated'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setCreateOpen(false);
      setCreateForm({ ...defaultCreateForm });
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('failedToCreateUser'));
      setCreateErrors({ [fieldFromMessage(message)]: message });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      const payload: UpdateUserPayload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone || undefined,
        isActive: editForm.isActive,
        locationId: editForm.locationId || null,
        commissionRate: editForm.commissionRate !== '' && editForm.commissionRate !== null ? Number(editForm.commissionRate) : null,
      };
      if (editForm.password) payload.password = editForm.password;
      // POS-I330: only a changed role is sent; it replaces the role the user holds now
      const heldRoles = selectedUser!.userRoles ?? [];
      if (heldRoles.length <= 1 && editForm.roleId && editForm.roleId !== (heldRoles[0]?.role.id ?? '')) payload.roleId = editForm.roleId;
      // POS-I185: a changed address is sent (and must be verified again); an unchanged one is left out
      const newEmail = editForm.email.trim();
      if (newEmail && newEmail.toLowerCase() !== (selectedUser!.email ?? '').toLowerCase()) payload.email = newEmail;
      const { data } = await api.patch(`/users/${selectedUser!.id}`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success(t('userUpdated'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditOpen(false);
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('failedToUpdateUser'));
      setEditErrors({ [fieldFromMessage(message)]: message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => {
      toast.success(t('userDeleted'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('deleteFailed')));
    },
  });

  // Reset password (admin)
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<User | null>(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  // POS-I185: refusals (own account, administrator, weak password) show under the field, not as a toast
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetShowPassword, setResetShowPassword] = useState(false);

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      api.patch(`/users/${userId}/reset-password`, { newPassword }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('passwordReset'));
      setResetPasswordOpen(false);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    },
    onError: (error: unknown) => {
      setResetPasswordError(getApiErrorMessage(error, t('failedToResetPassword')));
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.post(`/users/${userId}/roles`, { roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', selectedUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToAssignRole')));
    },
  });

  const removeRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.delete(`/users/${userId}/roles/${roleId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', selectedUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToRemoveRole')));
    },
  });

  /* ----- role toggle helper ----- */

  function toggleRole(role: Role) {
    if (!selectedUser) return;
    const hasRole = userRoles?.some((ur) => (ur.role?.id ?? ur.id) === role.id);
    if (hasRole) {
      removeRoleMutation.mutate({ userId: selectedUser.id, roleId: role.id });
    } else {
      assignRoleMutation.mutate({ userId: selectedUser.id, roleId: role.id });
    }
  }

  /* ----- render ----- */

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('usersTitle')}
        subtitle={t('usersSubtitle')}
        actions={canCreate && (
          <Button onClick={() => { setCreateForm({ ...defaultCreateForm }); setCreateErrors({}); setCreateOpen(true); }}>
            <Plus className="me-2 h-4 w-4" />
            {t('addUser')}
          </Button>
        )}
      />

      {/* User table */}
      <Card className="animate-fade-in-up">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder={t('searchUsers')}
            className="max-w-sm w-full"
          />
        </div>
        <CardContent className="p-0">
          {/* POS-I333: tighter cells so all eight columns, Actions included, fit at 1366 px */}
          <Table mobileCards className="[&_th]:px-3 [&_td]:px-3">
            <THead>
              <Tr hover={false}>
                <Th>{t('name')}</Th>
                <Th>{t('email')}</Th>
                <Th>{t('phone')}</Th>
                <Th>{t('location')}</Th>
                <Th>{t('rolesTitle')}</Th>
                <Th>{t('status')}</Th>
                <Th>{t('lastLogin')}</Th>
                <Th className="text-end">{t('actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={8} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="inline me-2 h-4 w-4 animate-spin" />
                    {t('loadingUsers')}
                  </Td>
                </Tr>
              ) : data?.data?.length === 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={8} className="py-8 text-center text-muted-foreground">
                    {t('noUsersFound')}
                  </Td>
                </Tr>
              ) : (
                data?.data?.map((user: User) => (
                  <Tr key={user.id}>
                    <Td label={t('name')} className="font-medium text-foreground">
                      {user.firstName} {user.lastName}
                    </Td>
                    <Td label={t('email')} className="break-all text-muted-foreground">{user.email}</Td>
                    <Td label={t('phone')} className="text-muted-foreground">{user.phone || '—'}</Td>
                    <Td label={t('location')}>
                      {user.location ? (
                        <span className="inline-flex items-center rounded-full bg-info/10 px-2 py-0.5 text-xs text-info-text">
                          {user.location.name}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t('allLocations')}</span>
                      )}
                    </Td>
                    <Td label={t('rolesTitle')}>
                      <div className="flex flex-wrap gap-1">
                        {user.userRoles?.length ? (
                          user.userRoles.map((ur) => (
                            <span
                              key={ur.role.id}
                              className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary-text"
                            >
                              {ur.role.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('noRoles')}</span>
                        )}
                      </div>
                    </Td>
                    <Td label={t('status')}>
                      <StatusBadge status={user.isActive ? 'ACTIVE' : 'INACTIVE'} />
                    </Td>
                    <Td label={t('lastLogin')} className="text-muted-foreground">
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : t('never')}
                    </Td>
                    <Td label={t('actions')} className="text-end td-actions">
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: t('editUser'),
                            icon: Edit,
                            onSelect: () => {
                              setSelectedUser(user);
                              setEditErrors({});
                              setEditOpen(true);
                            },
                            permission: canUpdate,
                            primary: true,
                          },
                          {
                            key: 'reset',
                            label: t('resetPassword'),
                            icon: KeyRound,
                            onSelect: () => {
                              setResetPasswordTarget(user);
                              setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                              setResetPasswordError(null);
                              setResetShowPassword(false);
                              setResetPasswordOpen(true);
                            },
                            permission: canUpdate,
                            primary: true,
                          },
                          {
                            key: 'roles',
                            label: t('manageRoles'),
                            icon: Shield,
                            onSelect: () => {
                              setSelectedUser(user);
                              setRolesOpen(true);
                            },
                          },
                          {
                            key: 'delete',
                            label: t('deleteUser'),
                            icon: Trash2,
                            onSelect: () =>
                              setDeleteConfirm({
                                open: true,
                                id: user.id,
                                name: `${user.firstName} ${user.lastName}`,
                              }),
                            permission: canDelete,
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {data?.meta && data.meta.total > 0 && (
        <div className="border-t px-4 py-3">
          <PaginationBar
            page={page}
            pageSize={20}
            total={data.meta.total ?? data.meta.totalPages * 20}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Create User Dialog */}
      <FormDialog
        open={createOpen}
        title={t('addUser')}
        width="52rem"
        maximizable
        noValidate
        submitLabel={t('createUser')}
        isPending={createMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={(e) => {
          e.preventDefault();
          const errs: FieldErrors = {};
          const email = createForm.email.trim();
          if (!email) errs.email = t('userEmailRequired');
          else if (!/^[^ @]+@[^ @]+[.][^ @]+$/.test(email)) errs.email = t('userEmailInvalid');
          if (!createForm.password) errs.password = t('userPasswordRequired');
          else if (createForm.password.length < 8) errs.password = t('passwordTooShort');
          if (!createForm.firstName.trim()) errs.firstName = t('userFirstNameRequired');
          if (!createForm.lastName.trim()) errs.lastName = t('userLastNameRequired');
          setCreateErrors(errs);
          if (Object.keys(errs).length) return;
          createMutation.mutate();
        }}
      >
        {createErrors.form && <p role="alert" className="text-sm text-destructive-text">{createErrors.form}</p>}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-email">{t('email')} *</Label>
            <Input
              id="create-email"
              type="email"
              required
              value={createForm.email}
              aria-invalid={!!createErrors.email}
              onChange={(e) => { setCreateForm({ ...createForm, email: e.target.value }); if (createErrors.email) setCreateErrors({ ...createErrors, email: undefined }); }}
              placeholder={t('emailPlaceholder')}
            />
            {createErrors.email && <p role="alert" className="text-xs text-destructive-text">{createErrors.email}</p>}
          </div>

              <div className="space-y-2">
                <Label htmlFor="create-password">{t('password')} *</Label>
                <Input
                  id="create-password"
                  type="password"
                  required
                  minLength={8}
                  value={createForm.password}
                  aria-invalid={!!createErrors.password}
                  onChange={(e) => {
                    setCreateForm({ ...createForm, password: e.target.value });
                    if (createErrors.password) setCreateErrors({ ...createErrors, password: undefined });
                  }}
                  placeholder={t('minCharsPlaceholder')}
                />
                {createErrors.password && <p role="alert" className="text-xs text-destructive-text">{createErrors.password}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-firstName">{t('firstName')} *</Label>
                  <Input
                    id="create-firstName"
                    required
                    value={createForm.firstName}
                    aria-invalid={!!createErrors.firstName}
                    onChange={(e) => {
                      setCreateForm({ ...createForm, firstName: e.target.value });
                      if (createErrors.firstName) setCreateErrors({ ...createErrors, firstName: undefined });
                    }}
                    placeholder={t('firstNamePlaceholder')}
                  />
                  {createErrors.firstName && <p role="alert" className="text-xs text-destructive-text">{createErrors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-lastName">{t('lastName')} *</Label>
                  <Input
                    id="create-lastName"
                    required
                    value={createForm.lastName}
                    aria-invalid={!!createErrors.lastName}
                    onChange={(e) => {
                      setCreateForm({ ...createForm, lastName: e.target.value });
                      if (createErrors.lastName) setCreateErrors({ ...createErrors, lastName: undefined });
                    }}
                    placeholder={t('lastNamePlaceholder')}
                  />
                  {createErrors.lastName && <p role="alert" className="text-xs text-destructive-text">{createErrors.lastName}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-phone">{t('phone')}</Label>
                <Input
                  id="create-phone"
                  dir="ltr"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder={t('phonePlaceholder')}
                />
              </div>

          <div className="space-y-2">
                <Label htmlFor="create-role">{t('initialRole')}</Label>
                <Combobox
                  options={(roles || []).map((role) => ({ value: role.id, label: role.name, sublabel: role.description }))}
                  value={createForm.roleId}
                  onChange={(val) => setCreateForm({ ...createForm, roleId: val })}
                  placeholder={t('selectRolePlaceholder')}
                  searchPlaceholder={t('searchRoles')}
                  emptyText={t('noRolesFound')}
                  allowClear
                />
              </div>

          <div className="space-y-2">
            <Label>{t('assignedLocation')}</Label>
            <Combobox
              options={(locations as { id: string; name: string }[]).map((l) => ({ value: l.id, label: l.name }))}
              value={createForm.locationId}
              onChange={(val) => setCreateForm({ ...createForm, locationId: val })}
              placeholder={t('allLocationsPlaceholder')}
              searchPlaceholder={t('searchLocations')}
              emptyText={t('noLocationsFound')}
              allowClear
            />
            <p className="text-xs text-muted-foreground">{t('locationHelpText')}</p>
          </div>

          <div className="space-y-2">
            <Label>{t('commissionRate', 'Commission Rate (%)')}</Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="0"
                value={createForm.commissionRate}
                onChange={(e) => setCreateForm({ ...createForm, commissionRate: e.target.value })}
              />
              <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('commissionRateHelp', 'Percentage of each sale credited as commission (0 = no commission)')}</p>
          </div>
        </div>
      </FormDialog>

      {/* Edit User Dialog */}
      <FormDialog
        open={editOpen && !!selectedUser}
        title={t('editUser')}
        width="52rem"
        maximizable
        noValidate
        submitLabel={t('updateUser')}
        isPending={editMutation.isPending}
        onClose={() => setEditOpen(false)}
        onSubmit={(e) => {
          e.preventDefault();
          const errs: FieldErrors = {};
          const email = editForm.email.trim();
          if (!email) errs.email = t('userEmailRequired');
          else if (!/^[^ @]+@[^ @]+[.][^ @]+$/.test(email)) errs.email = t('userEmailInvalid');
          if (!editForm.firstName.trim()) errs.firstName = t('userFirstNameRequired');
          if (!editForm.lastName.trim()) errs.lastName = t('userLastNameRequired');
          if (editForm.password && editForm.password.length < 8) errs.password = t('passwordTooShort');
          setEditErrors(errs);
          if (Object.keys(errs).length) return;
          editMutation.mutate();
        }}
      >
        {editErrors.form && <p role="alert" className="text-sm text-destructive-text">{editErrors.form}</p>}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-email">{t('email')}</Label>
            <Input
              id="edit-email"
              type="email"
              value={editForm.email}
              aria-invalid={!!editErrors.email}
              onChange={(e) => { setEditForm({ ...editForm, email: e.target.value }); if (editErrors.email) setEditErrors({ ...editErrors, email: undefined }); }}
              placeholder={t('emailPlaceholder')}
            />
            {editErrors.email && <p role="alert" className="text-xs text-destructive-text">{editErrors.email}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-firstName">{t('firstName')}</Label>
              <Input
                id="edit-firstName"
                value={editForm.firstName}
                aria-invalid={!!editErrors.firstName}
                onChange={(e) => {
                  setEditForm({ ...editForm, firstName: e.target.value });
                  if (editErrors.firstName) setEditErrors({ ...editErrors, firstName: undefined });
                }}
                placeholder={t('firstNamePlaceholder')}
              />
              {editErrors.firstName && <p role="alert" className="text-xs text-destructive-text">{editErrors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-lastName">{t('lastName')}</Label>
              <Input
                id="edit-lastName"
                value={editForm.lastName}
                aria-invalid={!!editErrors.lastName}
                onChange={(e) => {
                  setEditForm({ ...editForm, lastName: e.target.value });
                  if (editErrors.lastName) setEditErrors({ ...editErrors, lastName: undefined });
                }}
                placeholder={t('lastNamePlaceholder')}
              />
              {editErrors.lastName && <p role="alert" className="text-xs text-destructive-text">{editErrors.lastName}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-phone">{t('phone')}</Label>
            <Input
              id="edit-phone"
              dir="ltr"
              value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              placeholder={t('phonePlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-password">{t('newPassword')}</Label>
            <Input
              id="edit-password"
              type="password"
              value={editForm.password}
              aria-invalid={!!editErrors.password}
              onChange={(e) => { setEditForm({ ...editForm, password: e.target.value }); if (editErrors.password) setEditErrors({ ...editErrors, password: undefined }); }}
              placeholder={t('passwordKeepCurrent')}
            />
            {editErrors.password && <p role="alert" className="text-xs text-destructive-text">{editErrors.password}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-role">{t('editRoleLabel')}</Label>
            <Combobox
              options={(roles || []).map((role) => ({ value: role.id, label: role.name, sublabel: role.description }))}
              value={editForm.roleId}
              onChange={(val) => setEditForm({ ...editForm, roleId: val })}
              placeholder={t('selectRolePlaceholder')}
              searchPlaceholder={t('searchRoles')}
              emptyText={t('noRolesFound')}
              disabled={(selectedUser?.userRoles?.length ?? 0) > 1}
            />
            {(selectedUser?.userRoles?.length ?? 0) > 1 && (
              <p className="text-xs text-muted-foreground">{t('roleSeveralHint')}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Checkbox
              id="edit-active"
              checked={editForm.isActive}
              onChange={(e) =>
                setEditForm({ ...editForm, isActive: e.target.checked })
              }
            />
            <Label htmlFor="edit-active" className="cursor-pointer">
              {t('active')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label>{t('assignedLocation')}</Label>
            <Combobox
              options={(locations as { id: string; name: string }[]).map((l) => ({ value: l.id, label: l.name }))}
              value={editForm.locationId}
              onChange={(val) => setEditForm({ ...editForm, locationId: val })}
              placeholder={t('allLocationsPlaceholder')}
              searchPlaceholder={t('searchLocations')}
              emptyText={t('noLocationsFound')}
              allowClear
            />
            <p className="text-xs text-muted-foreground">{t('locationHelpTextShort')}</p>
          </div>

          <div className="space-y-2">
            <Label>{t('commissionRate', 'Commission Rate (%)')}</Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="0"
                value={editForm.commissionRate}
                onChange={(e) => setEditForm({ ...editForm, commissionRate: e.target.value })}
              />
              <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('commissionRateHelp', 'Percentage of each sale credited as commission')}</p>
          </div>
        </div>
      </FormDialog>

      {/* Manage Roles Dialog */}
      <Dialog
        visible={rolesOpen && !!selectedUser}
        onHide={() => setRolesOpen(false)}
        header={selectedUser ? t('manageRolesFor', { name: `${selectedUser.firstName} ${selectedUser.lastName}` }) : t('manageRoles')}
        footer={
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setRolesOpen(false)}>
              {t('done')}
            </Button>
          </div>
        }
        style={{ width: '52rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
      >
        {userRolesLoading? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="me-2 h-5 w-5 animate-spin" />
            {t('loadingRoles')}
          </div>
        ) : (
          <div className="space-y-2">
            {roles?.map((role) => {
              const hasRole = userRoles?.some((ur) => (ur.role?.id ?? ur.id) === role.id);
              const isMutating =
                assignRoleMutation.isPending || removeRoleMutation.isPending;

              return (
                <Label
                  key={role.id}
                  className="flex items-start gap-3 cursor-pointer rounded-lg border p-3 hover:bg-accent/50 transition-colors font-normal leading-normal text-foreground"
                >
                  <Checkbox
                    checked={!!hasRole}
                    disabled={isMutating}
                    onChange={() => toggleRole(role)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{role.name}</span>
                      {role.isSystem && (
                        <span className="inline-flex items-center rounded-full bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning-text">
                          {t('system')}
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {role.description}
                      </p>
                    )}
                  </div>
                </Label>
              );
            })}

            {!roles?.length && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('noRolesAvailable')}
              </p>
            )}
          </div>
        )}
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteUser')}
        message={t('deleteUserConfirm', { name: deleteConfirm.name })}
        confirmLabel={t('delete')}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '' }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />

      {/* Reset Password Dialog */}
      <FormDialog
        open={resetPasswordOpen}
        title={t('resetPassword')}
        width="36rem"
        icon={<KeyRound className="h-5 w-5 text-warning-text" />}
        submitLabel={t('resetPasswordBtn')}
        isPending={resetPasswordMutation.isPending}
        onClose={() => { setResetPasswordOpen(false); setResetPasswordForm({ newPassword: '', confirmPassword: '' }); }}
        onSubmit={(e) => {
          e.preventDefault();
          setResetPasswordError(null);
          if (resetPasswordForm.newPassword.length < 8) { setResetPasswordError(t('passwordTooShort')); return; }
          if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) { setResetPasswordError(t('passwordsMismatch')); return; }
          resetPasswordMutation.mutate({ userId: resetPasswordTarget!.id, newPassword: resetPasswordForm.newPassword });
        }}
      >
        <div className="space-y-4">
          {resetPasswordTarget && (
            <p className="text-sm text-muted-foreground">
              {t('resetPasswordFor')}{' '}
              <span className="font-semibold text-foreground">
                {resetPasswordTarget.firstName} {resetPasswordTarget.lastName}
              </span>{' '}
              ({resetPasswordTarget.email})
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="reset-new-password">{t('newPassword')} *</Label>
            <Input
              id="reset-new-password"
              type={resetShowPassword ? 'text' : 'password'}
              minLength={8}
              value={resetPasswordForm.newPassword}
              onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
              placeholder={t('minCharsPlaceholder')}
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-confirm-password">{t('confirmPassword')} *</Label>
            <Input
              id="reset-confirm-password"
              type={resetShowPassword ? 'text' : 'password'}
              value={resetPasswordForm.confirmPassword}
              onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
              placeholder={t('repeatPasswordPlaceholder')}
              autoComplete="new-password"
            />
          </div>
          <button
            type="button"
            className="text-xs font-medium text-primary-text hover:underline"
            onClick={() => setResetShowPassword((v) => !v)}
          >
            {resetShowPassword ? t('hidePassword') : t('showPassword')}
          </button>
          {resetPasswordError && (
            <p role="alert" className="text-sm text-destructive-text">{resetPasswordError}</p>
          )}
          <p className="text-xs text-warning-text">
            {t('resetPasswordWarning')}
          </p>
        </div>
      </FormDialog>
    </div>
  );
}
